import React from 'react';
import { useController } from 'react-hook-form';

/**
 * Adapter that bridges react-hook-form's Controller API to the redux-form
 * Field interface ({input, meta}) expected by existing field components.
 *
 * Usage (drop-in replacement for redux-form's <Field>):
 *   <HookFormField name="email" component={Input} control={control} validate={[required]} />
 */
const HookFormField = ({ name, component: Component, control, validate, ...rest }) => {
    const rules = {};
    if (validate) {
        const validators = Array.isArray(validate) ? validate : [validate];
        rules.validate = (value) => {
            for (const v of validators) {
                const error = v(value);
                if (error) return error;
            }
            return true;
        };
    }

    const { field, fieldState } = useController({ name, control, rules });

    const input = {
        ...field,
        onBlur: field.onBlur,
        onFocus: () => {},
    };

    const meta = {
        touched: fieldState.isTouched || !!fieldState.error,
        error: fieldState.error?.message || (typeof fieldState.error?.type === 'string' && fieldState.error.type !== 'validate' ? fieldState.error.type : undefined),
    };

    // Resolve validate-function errors stored as the validate return value
    if (fieldState.error && fieldState.error.type === 'validate' && fieldState.error.message === '') {
        const validationResult = rules.validate?.(field.value);
        if (typeof validationResult === 'string') {
            meta.error = validationResult;
        }
    }

    return <Component input={input} meta={meta} {...rest} />;
};

export default HookFormField;
